import React, { useState, ReactNode } from 'react';
import { Button } from 'components/ui/button';
import { FiPrinter } from 'react-icons/fi';
import { BASE_URL } from 'app/constants';
import { ProductOrder } from 'app/models/ProductOrder';

interface SharedProps {
  className?: string;
  variant?: 'default' | 'outline' | 'secondary';
  buttonLabel?: string;
  icon?: ReactNode;
  documentTitle?: string;
  tooltip?: string;
  tooltipId?: string;
}

interface Props extends SharedProps {
  product: ProductOrder;
}

const AssemblyNoticeButton: React.FC<Props> = ({
  product,
  className = '',
  variant = 'outline',
  buttonLabel = 'Imprimer la notice',
  icon,
  documentTitle,
  tooltip,
  tooltipId = 'infos',
}) => {
  const [isProcessing, setIsProcessing] = useState(false);

  if (!product.instructions) {
    return null;
  }

  const resolveInstructionUrl = () => {
    if (!product.instructions) return undefined;

    try {
      return new URL(product.instructions, BASE_URL).href;
    } catch (error) {
      console.warn(
        `Failed to resolve instruction URL for product ${product.id}, falling back to raw value`,
        error,
      );
      return product.instructions;
    }
  };

  const triggerDownload = async () => {
    const instructionUrl = resolveInstructionUrl();
    if (!instructionUrl) return;

    try {
      const token = localStorage.getItem('token');
      const headers: Record<string, string> = {};

      if (token) {
        headers['Authorization'] = `JWT ${token}`;
      }

      const response = await fetch(instructionUrl, {
        headers,
        credentials: 'same-origin',
      });

      if (!response.ok) {
        console.warn(
          `Failed to download instruction PDF: ${response.statusText}`,
        );
        return;
      }

      const blob = await response.blob();
      if (blob.type !== 'application/pdf' && blob.size === 0) {
        console.warn('Received empty instruction PDF.');
        return;
      }

      const urlObject = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const fileNameFromUrl = instructionUrl.split('/').pop();
      const preferredFileName = documentTitle
        ? documentTitle.endsWith('.pdf')
          ? documentTitle
          : `${documentTitle}.pdf`
        : undefined;
      const fileName =
        preferredFileName ??
        (fileNameFromUrl && fileNameFromUrl.endsWith('.pdf')
          ? fileNameFromUrl
          : `Notice-Assemblage-${product.id}.pdf`);
      link.href = urlObject;
      link.download = fileName;
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(urlObject);
    } catch (error) {
      console.error(
        `Error downloading instruction PDF for product ${product.id}:`,
        error,
      );
    }
  };

  const handleDownload = async () => {
    setIsProcessing(true);
    try {
      await triggerDownload();
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Button
      onClick={handleDownload}
      disabled={isProcessing}
      variant={variant}
      className={className}
      type="button"
      data-tip={tooltip}
      data-for={tooltip && tooltipId ? tooltipId : undefined}
    >
      {icon ?? <FiPrinter className="mr-2 h-4 w-4" />}
      {isProcessing ? 'Téléchargement...' : buttonLabel}
    </Button>
  );
};

export default AssemblyNoticeButton;
